import { functions } from './firebaseClient';
import { httpsCallable } from 'firebase/functions';
import { getFunctionsUrl } from './firebaseClient';

/**
 * Functions Helper - Adaptador para invocar Cloud Functions
 * 
 * Proporciona una API similar a base44.functions.invoke usando Firebase Functions
 */

class FunctionsHelper {
  /**
   * Invocar una Cloud Function
   * Similar a base44.functions.invoke(functionName, data)
   * 
   * @param {string} functionName - Nombre de la función
   * @param {Object} data - Datos a enviar a la función
   * @returns {Promise<any>} Respuesta de la función
   */
  async invoke(functionName, data = {}) {
    try {
      // Usar httpsCallable para funciones HTTP de segunda generación
      const callable = httpsCallable(functions, functionName);
      const result = await callable(data);
      
      return result.data;
    } catch (error) {
      console.error(`Error invoking function ${functionName}:`, error);
      
      // Si falla con callable, intentar con fetch directo (para funciones HTTP)
      try {
        const url = `${getFunctionsUrl()}/${functionName}`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data)
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        return await response.json();
      } catch (fetchError) {
        console.error(`Error with fetch fallback for ${functionName}:`, fetchError);
        throw error; // Lanzar el error original
      }
    }
  }

  /**
   * Invocar una función HTTP directamente con fetch
   * Útil para funciones que no son callable
   * 
   * @param {string} functionName - Nombre de la función
   * @param {Object} data - Datos a enviar
   * @param {Object} options - Opciones adicionales de fetch
   * @returns {Promise<any>}
   */
  async invokeHttp(functionName, data = {}, options = {}) {
    try {
      const url = `${getFunctionsUrl()}/${functionName}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        body: JSON.stringify(data),
        ...options
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        return await response.text();
      }
    } catch (error) {
      console.error(`Error invoking HTTP function ${functionName}:`, error);
      throw error;
    }
  }
}

// Exportar instancia singleton
export const functionsHelper = new FunctionsHelper();

export default functionsHelper;
